// Files picked from iCloud / Files on iOS often arrive with no extension and an
// empty File.type (e.g. "IMG_4733"). Sending that through meant fileType: '' in
// the upload request, which the API rejected with a 500 (PT-7025). The backend
// also derives data.extension from the file name and drives thumbnails off it,
// so both the name and the MIME type have to be filled in for the file to be
// usable afterwards — guessing the MIME alone is not enough.

interface SignaturePart {
    offset: number;
    bytes: number[];
}

interface FileSignature {
    mime: string;
    extension: string;
    // Every part must match for the signature to apply.
    parts: SignaturePart[];
}

const asciiBytes = (text: string): number[] =>
    [...text].map((char) => char.charCodeAt(0));

// ISO base media brands used by photos coming straight off an iPhone. Each one
// states its own mime and extension — adding a brand must not depend on how the
// existing ones happen to be spelled.
const ISO_BASE_MEDIA_BRANDS: {
    brand: string;
    mime: string;
    extension: string;
}[] = [
    { brand: 'heic', mime: 'image/heic', extension: 'heic' },
    { brand: 'heix', mime: 'image/heic', extension: 'heic' },
    { brand: 'hevc', mime: 'image/heic', extension: 'heic' },
    { brand: 'hevx', mime: 'image/heic', extension: 'heic' },
    { brand: 'mif1', mime: 'image/heif', extension: 'heif' },
    { brand: 'msf1', mime: 'image/heif', extension: 'heif' },
];

const brandSignature = ({
    brand,
    mime,
    extension,
}: (typeof ISO_BASE_MEDIA_BRANDS)[number]): FileSignature => ({
    mime,
    extension,
    parts: [
        { offset: 4, bytes: asciiBytes('ftyp') },
        { offset: 8, bytes: asciiBytes(brand) },
    ],
});

const SIGNATURES: FileSignature[] = [
    {
        mime: 'image/jpeg',
        extension: 'jpg',
        parts: [{ offset: 0, bytes: [0xff, 0xd8, 0xff] }],
    },
    {
        mime: 'image/png',
        extension: 'png',
        parts: [
            {
                offset: 0,
                bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
            },
        ],
    },
    {
        mime: 'image/gif',
        extension: 'gif',
        parts: [{ offset: 0, bytes: asciiBytes('GIF8') }],
    },
    {
        mime: 'image/webp',
        extension: 'webp',
        parts: [
            { offset: 0, bytes: asciiBytes('RIFF') },
            { offset: 8, bytes: asciiBytes('WEBP') },
        ],
    },
    {
        mime: 'application/pdf',
        extension: 'pdf',
        parts: [{ offset: 0, bytes: asciiBytes('%PDF') }],
    },
    ...ISO_BASE_MEDIA_BRANDS.map(brandSignature),
];

// Enough for the longest signature above (brand at offset 8 plus four bytes).
const SIGNATURE_BYTE_COUNT = 16;

// Only for files that already carry an extension but no MIME type — the
// extension is the user's stated intent, so we trust it rather than sniffing.
const MIME_BY_EXTENSION: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    heic: 'image/heic',
    heif: 'image/heif',
    pdf: 'application/pdf',
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    txt: 'text/plain',
    csv: 'text/csv',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

function getExtension(fileName: string): string | undefined {
    const match = /\.([a-z0-9]+)$/i.exec(fileName);
    return match?.[1].toLowerCase();
}

function matches(header: Uint8Array, signature: FileSignature): boolean {
    return signature.parts.every(
        ({ offset, bytes }) =>
            offset + bytes.length <= header.length &&
            bytes.every((byte, index) => header[offset + index] === byte),
    );
}

async function detectFromContent(
    file: File,
): Promise<FileSignature | undefined> {
    try {
        const header = new Uint8Array(
            await file.slice(0, SIGNATURE_BYTE_COUNT).arrayBuffer(),
        );
        return SIGNATURES.find((signature) => matches(header, signature));
    } catch {
        // Unreadable slice (file moved or permissions revoked mid-pick) — the
        // upload itself would fail anyway, so fall through to "unrecognised".
        return undefined;
    }
}

/**
 * Fills in whatever the browser left out. Returns a file that is safe to upload
 * — name with an extension, non-empty type — or undefined when the type cannot
 * be established, in which case the caller should reject the file rather than
 * upload something nobody will be able to open.
 */
export async function normalizeFileForUpload(
    file: File,
): Promise<File | undefined> {
    const extension = getExtension(file.name);
    if (file.type && extension) {
        return file;
    }

    const detected = await detectFromContent(file);
    if (detected) {
        return withNameAndType(
            file,
            extension ? file.name : `${file.name}.${detected.extension}`,
            file.type || detected.mime,
        );
    }

    const mimeFromExtension = extension && MIME_BY_EXTENSION[extension];
    if (mimeFromExtension) {
        return withNameAndType(file, file.name, mimeFromExtension);
    }

    return undefined;
}

// File is immutable, so a corrected name/type means a new instance. The content
// is passed by reference, nothing is copied.
function withNameAndType(file: File, name: string, type: string): File {
    return new File([file], name, { type, lastModified: file.lastModified });
}
